import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import type { DatiTargaResponse } from '@/types'

interface MarcheResponse {
  marche: string[]
  count: number
}

interface ModelliResponse {
  marca: string
  modelli: string[]
  count: number
}

interface CarburantiResponse {
  carburanti: string[]
}

export interface ModelloCustomItem {
  id: number
  nome: string
}

export interface MarcaCustom {
  id: number
  nome: string
  modelli: ModelloCustomItem[]
}

export interface ModelloCustom {
  id: number
  marca_id: number
  nome: string
}

/**
 * Hook per ottenere la lista delle marche auto disponibili
 */
export const useMarche = () => {
  return useQuery({
    queryKey: ['auto', 'marche'],
    queryFn: async () => {
      const response = await axiosInstance.get<MarcheResponse>(API_ENDPOINTS.AUTO_MARCHE)
      return response.data
    },
    staleTime: 1000 * 60 * 60 * 24, // 24h
  })
}

/**
 * Hook per ottenere i modelli di una specifica marca
 */
export const useModelli = (marca: string) => {
  return useQuery({
    queryKey: ['auto', 'modelli', marca],
    queryFn: async () => {
      const response = await axiosInstance.get<ModelliResponse>(
        API_ENDPOINTS.AUTO_MODELLI(marca)
      )
      return response.data
    },
    enabled: !!marca,
    staleTime: 1000 * 60 * 60 * 24, // 24h
  })
}

export const useMarcheCustom = () => {
  return useQuery({
    queryKey: ['auto', 'marche-custom'],
    queryFn: async () => {
      const res = await axiosInstance.get<MarcaCustom[]>(API_ENDPOINTS.AUTO_MARCHE_CUSTOM)
      return res.data
    },
    staleTime: 0,
  })
}

export const useCreateMarcaCustom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (nome: string) =>
      axiosInstance.post<MarcaCustom>(API_ENDPOINTS.AUTO_MARCHE_CUSTOM, { nome }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto'] })
    },
  })
}

export const useDeleteMarcaCustom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      axiosInstance.delete(API_ENDPOINTS.AUTO_MARCHE_CUSTOM_DELETE(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto'] })
    },
  })
}

export const useCreateModelloCustom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ marca_id, nome }: { marca_id: number; nome: string }) =>
      axiosInstance.post<ModelloCustom>(API_ENDPOINTS.AUTO_MODELLI_CUSTOM, { marca_id, nome }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto'] })
    },
  })
}

export const useDeleteModelloCustom = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      axiosInstance.delete(API_ENDPOINTS.AUTO_MODELLI_CUSTOM_DELETE(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto'] })
    },
  })
}

interface SyncResult {
  aggiunte: number
  totale: number
}

export const useSeedMarcheDefault = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      axiosInstance.post<SyncResult>(API_ENDPOINTS.AUTO_SEED_MARCHE).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto'] })
    },
  })
}

export const useSyncCarQuery = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      axiosInstance.post<SyncResult>(API_ENDPOINTS.AUTO_SYNC_CARQUERY).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto'] })
    },
  })
}

/**
 * Hook per ottenere la lista dei tipi di carburante
 */
export const useCarburanti = () => {
  return useQuery({
    queryKey: ['auto', 'carburanti'],
    queryFn: async () => {
      const response = await axiosInstance.get<CarburantiResponse>(API_ENDPOINTS.AUTO_CARBURANTI)
      return response.data.carburanti
    },
    staleTime: 1000 * 60 * 60 * 24, // 24 ore
  })
}

/**
 * Hook mutation per verificare una targa e ottenere i dati del veicolo
 */
export const useVerificaTarga = () => {
  return useMutation({
    mutationFn: async (targa: string): Promise<DatiTargaResponse> => {
      const response = await axiosInstance.post<DatiTargaResponse>(
        API_ENDPOINTS.AUTO_VERIFICA_TARGA,
        { targa: targa.toUpperCase().replace(/\s/g, '') }
      )
      return response.data
    },
  })
}
